import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";
import { distanceBetweenCoordinates } from "./distanceUtils";

// Synthetic id prefix shared with the Android app so custom places flow through the normal
// place pipeline (place objects keyed by place_id) without any schema changes.
export const CUSTOM_PLACE_PREFIX = "custom:";

// Max distance (meters) for a custom place to be suggested for a photo.
export const CUSTOM_MATCH_RADIUS_METERS = 150;

export const isCustomPlaceId = (placeId) =>
  typeof placeId === "string" && placeId.startsWith(CUSTOM_PLACE_PREFIX);

// Loads the signed-in user's custom places from users/{uid}/customPlaces.
export const loadCustomPlaces = async (userId = null) => {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) return [];

  try {
    const snapshot = await getDocs(collection(db, `users/${uid}/customPlaces`));
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        place_id: CUSTOM_PLACE_PREFIX + docSnap.id,
        name: data.name || "",
        latitude: data.latitude,
        longitude: data.longitude,
        types: ["custom_place"],
      };
    });
  } catch (error) {
    console.error("Error loading custom places:", error);
    return [];
  }
};

// Simple in-memory cache so a single popup open doesn't re-read Firestore for every photo group.
let cachedUid = null;
let cachedPlacesPromise = null;

export const getCustomPlacesCached = (forceReload = false) => {
  const uid = auth.currentUser?.uid || null;
  if (forceReload || uid !== cachedUid || !cachedPlacesPromise) {
    cachedUid = uid;
    cachedPlacesPromise = loadCustomPlaces(uid);
  }
  return cachedPlacesPromise;
};

// Returns custom places within CUSTOM_MATCH_RADIUS_METERS of the given lat/lng.
export const customPlacesNear = (customPlaces, latitude, longitude) => {
  if (latitude == null || longitude == null) return [];
  if (!Array.isArray(customPlaces)) return [];
  return customPlaces
    .map((place) => ({
      place,
      distanceMeters:
        place.latitude != null && place.longitude != null
          ? distanceBetweenCoordinates(
              { latitude, longitude },
              { latitude: place.latitude, longitude: place.longitude }
            )
          : Number.POSITIVE_INFINITY,
    }))
    .filter(({ distanceMeters }) => distanceMeters <= CUSTOM_MATCH_RADIUS_METERS)
    .sort(
      (a, b) =>
        a.distanceMeters - b.distanceMeters ||
        (a.place.name || "").localeCompare(b.place.name || "")
    )
    .map(({ place }) => place);
};
